import { useState, useCallback, useEffect, useRef } from "react";

interface UseKeyboardNavigationOptions {
  /**
   * Total number of items to navigate
   */
  itemCount: number;
  /**
   * Whether navigation is enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * Callback when an item is selected (Enter key)
   */
  onSelect?: (index: number) => void;
  /**
   * Callback when navigation changes (for highlighting)
   */
  onNavigate?: (index: number) => void;
  /**
   * Whether to loop from last to first and vice versa
   * @default true
   */
  loop?: boolean;
  /**
   * Initial selected index
   * @default -1 (no selection)
   */
  initialIndex?: number;
}

/**
 * Hook for keyboard navigation (arrow keys, Enter, Escape)
 * 
 * @example
 * ```tsx
 * const { selectedIndex, handleKeyDown } = useKeyboardNavigation({
 *   itemCount: suggestions.length,
 *   onSelect: (index) => handleSelect(suggestions[index]),
 * });
 * 
 * <input onKeyDown={handleKeyDown} />
 * ```
 */
export function useKeyboardNavigation({
  itemCount,
  enabled = true,
  onSelect,
  onNavigate,
  loop = true,
  initialIndex = -1,
}: UseKeyboardNavigationOptions) {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const selectedIndexRef = useRef(selectedIndex);

  // Keep ref in sync
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  // Reset selection when item count changes
  useEffect(() => {
    if (itemCount === 0) {
      setSelectedIndex(-1);
    } else if (selectedIndex >= itemCount) {
      setSelectedIndex(Math.max(0, itemCount - 1));
    }
  }, [itemCount, selectedIndex]);

  const navigate = useCallback(
    (direction: "up" | "down") => {
      if (!enabled || itemCount === 0) return;

      setSelectedIndex((current) => {
        let next: number;

        if (direction === "down") {
          if (current === -1 || current === itemCount - 1) {
            next = loop ? 0 : Math.min(current + 1, itemCount - 1);
          } else {
            next = current + 1;
          }
        } else {
          // up
          if (current === -1 || current === 0) {
            next = loop ? itemCount - 1 : Math.max(current - 1, 0);
          } else {
            next = current - 1;
          }
        }

        onNavigate?.(next);
        return next;
      });
    },
    [enabled, itemCount, loop, onNavigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!enabled) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          navigate("down");
          break;
        case "ArrowUp":
          e.preventDefault();
          navigate("up");
          break;
        case "Enter":
          if (selectedIndexRef.current >= 0 && selectedIndexRef.current < itemCount) {
            e.preventDefault();
            onSelect?.(selectedIndexRef.current);
          }
          break;
        case "Escape":
          setSelectedIndex(-1);
          break;
      }
    },
    [enabled, navigate, itemCount, onSelect]
  );

  const reset = useCallback(() => {
    setSelectedIndex(initialIndex);
  }, [initialIndex]);

  return {
    selectedIndex,
    handleKeyDown,
    reset,
    setSelectedIndex,
  };
}

